import { type ProColumns, ProTable, type ProTableProps } from "@ant-design/pro-components";
import { Typography } from "antd";
import type { Key, ReactNode } from "react";
import { AdminListScaffold, buildAdminProTableCommonProps } from "./admin-table";

export function readAdminPickerSelectionKeys(keys: readonly Key[]): Array<string | number> {
  return keys.filter(
    (key): key is string | number => typeof key === "string" || typeof key === "number",
  );
}

type AdminPickerWorkspaceReservedTableProp =
  | "key"
  | "columns"
  | "columnsState"
  | "locale"
  | "options"
  | "rowKey"
  | "rowSelection"
  | "search"
  | "tableAlertOptionRender"
  | "tableAlertRender"
  | "tableLayout";

export type AdminPickerWorkspaceTableProps<
  T extends object,
  U extends object = Record<string, unknown>,
> = Omit<ProTableProps<T, U>, AdminPickerWorkspaceReservedTableProp>;

export type AdminPickerWorkspaceProps<
  T extends object,
  U extends object = Record<string, unknown>,
> = {
  columns: readonly ProColumns<T>[];
  header?: ReactNode;
  tableKey?: Key;
  rowKey: NonNullable<ProTableProps<T, U>["rowKey"]>;
  persistenceKey?: string;
  defaultColumnsState?: Record<string, { show: boolean }>;
  showToolbarOptions?: boolean;
  emptyText?: ReactNode;
  tableLayout?: "auto" | "fixed";
  tableProps?: AdminPickerWorkspaceTableProps<T, U>;
  selectedRowKeys?: readonly (string | number)[];
  onSelectionChange?: (keys: Array<string | number>, rows: T[]) => void;
  rowSelectionType?: "radio" | "checkbox";
  getCheckboxProps?: (record: T) => { disabled?: boolean };
  preserveSelectedRowKeys?: boolean;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  showTableSkeleton?: boolean;
};

export function AdminPickerWorkspace<T extends object, U extends object = Record<string, unknown>>({
  columns,
  header,
  tableKey,
  rowKey,
  persistenceKey,
  defaultColumnsState,
  showToolbarOptions = true,
  emptyText,
  tableLayout = "auto",
  tableProps,
  selectedRowKeys = [],
  onSelectionChange,
  rowSelectionType = "radio",
  getCheckboxProps,
  preserveSelectedRowKeys,
  footerLeft,
  footerRight,
  showTableSkeleton,
}: AdminPickerWorkspaceProps<T, U>) {
  const tablePropsWithoutReserved = tableProps
    ? (tableProps as Omit<ProTableProps<T, U>, "rowKey">)
    : undefined;
  const commonTableProps = persistenceKey
    ? buildAdminProTableCommonProps<T, U>({
        persistenceKey,
        showToolbarOptions,
        ...(defaultColumnsState ? { defaultColumnsState } : {}),
      })
    : undefined;
  const mergedTableScroll = tableProps?.scroll
    ? { x: "max-content", ...tableProps.scroll }
    : { x: "max-content" as const };
  const normalizedColumns = columns.slice();
  const locale =
    emptyText === undefined
      ? commonTableProps?.locale
      : {
          ...commonTableProps?.locale,
          emptyText: <Typography.Text type="secondary">{emptyText}</Typography.Text>,
        };

  return (
    <AdminListScaffold
      {...(showTableSkeleton === undefined ? {} : { showTableSkeleton })}
      {...(footerLeft === undefined ? {} : { footerLeft })}
      {...(footerRight === undefined ? {} : { footerRight })}
      table={
        <>
          {header}
          <ProTable<T, U>
            key={tableKey}
            rowKey={rowKey}
            search={false}
            tableLayout={tableLayout}
            columns={normalizedColumns}
            tableAlertRender={false}
            tableAlertOptionRender={false}
            options={commonTableProps?.options ?? false}
            {...(commonTableProps?.columnsState ? { columnsState: commonTableProps.columnsState } : {})}
            {...(locale === undefined ? {} : { locale })}
            {...(tablePropsWithoutReserved ? tablePropsWithoutReserved : {})}
            scroll={mergedTableScroll}
            {...(onSelectionChange
              ? {
                  rowSelection: {
                    type: rowSelectionType,
                    ...(getCheckboxProps ? { getCheckboxProps } : {}),
                    ...(preserveSelectedRowKeys === undefined ? {} : { preserveSelectedRowKeys }),
                    selectedRowKeys: Array.from(selectedRowKeys),
                    onChange: (keys: Key[], rows: T[]) => {
                      onSelectionChange(readAdminPickerSelectionKeys(keys), rows);
                    },
                  },
                }
              : {})}
          />
        </>
      }
    />
  );
}
